import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useOrganizationList, useOrganization as useClerkOrganization, useUser } from '@clerk/clerk-react';
import { supabase } from '@/lib/supabase';

interface Organization {
  id: string;
  name: string;
  owner_id: string;
  created_at: string;
  updated_at: string;
  role?: string;
}

interface OrganizationMember {
  user_id: string;
  organization_id: string;
  role: 'owner' | 'admin' | 'member';
  user?: {
    email: string;
    full_name: string;
    avatar_url?: string;
  };
}

interface OrganizationContextType {
  currentOrganization: Organization | null;
  organizations: Organization[];
  members: OrganizationMember[];
  loading: boolean;
  needsOnboarding: boolean;
  switchOrganization: (orgId: string) => Promise<void>;
  createOrganization: (name: string) => Promise<Organization | null>;
  updateOrganization: (name: string) => Promise<void>;
  inviteUser: (email: string, role: 'admin' | 'member') => Promise<void>;
  updateMemberRole: (userId: string, role: 'admin' | 'member') => Promise<void>;
  removeMember: (userId: string) => Promise<void>;
  refreshOrganizations: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

// Helper function to sync Clerk organization to Supabase
async function syncOrganizationToSupabase(clerkOrg: any, clerkUser: any): Promise<boolean> {
  if (!clerkOrg || !clerkUser) {
    return false;
  }
  
  
  try {
    // FIRST: Ensure user exists in Supabase
    let { data: existingUser, error: userFetchError } = await supabase
      .from('users')
      .select('id')
      .eq('clerk_id', clerkUser.id)
      .maybeSingle(); // Use maybeSingle instead of single to avoid 406 errors
    
    if (!existingUser) {
      
      // Generate a UUID for the user
      const userId = crypto.randomUUID();
      
      // Insert user with generated UUID
      const { data: newUser, error: userError } = await supabase
        .from('users')
        .insert({
          id: userId,
          email: clerkUser.primaryEmailAddress?.emailAddress,
          full_name: clerkUser.fullName,
          clerk_id: clerkUser.id,
          created_at: new Date(clerkUser.createdAt).toISOString(),
          updated_at: new Date(clerkUser.updatedAt).toISOString()
        })
        .select('id')
        .single();
      
      if (userError) {
        // console.error('❌ Error creating user:', userError);
        return false;
      }
      
      existingUser = newUser;
    }
    
    // SECOND: Check if organization exists in Supabase
    let { data: existingOrg, error: orgFetchError } = await supabase
      .from('organizations')
      .select('id')
      .eq('clerk_id', clerkOrg.id)
      .maybeSingle(); // Use maybeSingle to avoid errors
    
    if (!existingOrg) {
      
      // Generate UUID for organization
      const orgId = crypto.randomUUID();
      
      // Insert organization with the actual user ID from Supabase
      const { data: newOrg, error: orgError } = await supabase
        .from('organizations')
        .insert({
          id: orgId,
          name: clerkOrg.name,
          owner_id: existingUser.id, // Use the Supabase user ID, not Clerk ID
          clerk_id: clerkOrg.id,
          created_at: new Date(clerkOrg.createdAt).toISOString(),
          updated_at: new Date(clerkOrg.updatedAt).toISOString()
        })
        .select('id')
        .single();
      
      if (orgError) {
        // console.error('❌ Error creating organization:', orgError);
        return false;
      }
      
      existingOrg = newOrg;
    }
    
    // Check if membership exists (use the actual IDs from Supabase)
    const { data: existingMembership } = await supabase
      .from('organization_members')
      .select('user_id')
      .eq('user_id', existingUser.id)
      .eq('organization_id', existingOrg.id)
      .maybeSingle();
    
    if (!existingMembership) {
      
      // Insert membership
      const { error: memberError } = await supabase
        .from('organization_members')
        .insert({
          user_id: existingUser.id,
          organization_id: existingOrg.id,
          role: 'owner', // Creator is always owner in Supabase
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
      
      if (memberError) {
        // console.error('❌ Error creating membership:', memberError);
        return false;
      }
    }
    
    return true;
    
  } catch (error) {
    // console.error('❌ Error syncing organization to Supabase:', error);
    return false;
  }
}

export function OrganizationProvider({ children }: { children: React.ReactNode }) {
  const { user, isLoaded: userLoaded } = useUser();
  const { organizationList, isLoaded: orgsLoaded, createOrganization: clerkCreateOrg, setActive } = useOrganizationList({
    userMemberships: {
      infinite: true,
      pageSize: 10
    }
  });
  const { organization: activeOrg, membership, inviteMember } = useClerkOrganization();

  // Debug effect to track all Clerk state changes
  useEffect(() => {
  }, [userLoaded, orgsLoaded, user, organizationList, activeOrg, membership]);
  
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  // Timeout fallback to prevent infinite loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        setLoading(false);
        // If still no organizations after timeout, assume needs onboarding
        if (!organizationList || organizationList.length === 0) {
          setNeedsOnboarding(true);
        }
      }
    }, 10000); // 10 second timeout

    return () => clearTimeout(timeout);
  }, [loading, organizationList]);

  // Add a manual refresh function
  const refreshOrganizations = useCallback(async () => {
    
    // Small delay to allow Clerk to update
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Force a re-sync by incrementing a counter
    setLoading(true);
    setTimeout(() => setLoading(false), 100);
  }, []);

  // Sync Clerk organizations with local state
  useEffect(() => {
    const syncOrganizations = async () => {
      
      // Only proceed if both user and orgs are loaded
      if (!userLoaded || !orgsLoaded) {
        return;
      }

      // Detailed debugging of organizationList
      if (organizationList) {
      } else {
      }

      // Special case: If organizationList is null/undefined but we have an active organization,
      // the user has organizations but useOrganizationList isn't working properly
      if ((!organizationList || organizationList.length === 0) && activeOrg) {
        
        // Sync organization to Supabase if it doesn't exist
        const syncSuccess = await syncOrganizationToSupabase(activeOrg, user);
        if (!syncSuccess) {
          // console.error('Failed to sync organization to Supabase');
        }
        
        // Create a minimal organization object from activeOrg
        const orgFromActive: Organization = {
          id: activeOrg.id,
          name: activeOrg.name,
          owner_id: activeOrg.createdBy,
          created_at: new Date(activeOrg.createdAt).toISOString(),
          updated_at: new Date(activeOrg.updatedAt).toISOString(),
          role: membership?.role
        };
        
        setOrganizations([orgFromActive]);
        setCurrentOrganization(orgFromActive);
        setNeedsOnboarding(false);
        setLoading(false);
        return;
      }

      // Process the organizations
      if (!organizationList || organizationList.length === 0) {
        setNeedsOnboarding(true);
        setOrganizations([]);
        setCurrentOrganization(null);
        setLoading(false);
        return;
      }

      
      // Sync each organization to Supabase
      for (const orgMembership of organizationList) {
        const syncSuccess = await syncOrganizationToSupabase(orgMembership.organization, user);
        if (!syncSuccess) {
          // console.error('Failed to sync organization:', orgMembership.organization.id);
        }
      }
      
      const orgs: Organization[] = organizationList.map(orgMembership => ({
        id: orgMembership.organization.id,
        name: orgMembership.organization.name,
        owner_id: orgMembership.organization.createdBy,
        created_at: new Date(orgMembership.organization.createdAt).toISOString(),
        updated_at: new Date(orgMembership.organization.updatedAt).toISOString(),
        role: orgMembership.membership.role
      }));


      setOrganizations(orgs);
      setNeedsOnboarding(false);
      
      // Set active organization
      if (activeOrg) {
        const current = orgs.find(o => o.id === activeOrg.id);
        if (current) {
          setCurrentOrganization(current);
        }
      } else if (orgs.length > 0) {
        // Default to first organization
        setActive({ organization: orgs[0].id });
        setCurrentOrganization(orgs[0]);
      }
      
      setLoading(false);
      };

    syncOrganizations();
  }, [userLoaded, orgsLoaded, organizationList, activeOrg, setActive, user]);

  // Sync members when organization changes
  useEffect(() => {
    if (activeOrg && activeOrg.membersCount) {
      // In a real implementation, you'd fetch members from Clerk
      // For now, we'll just set the current user as a member
      if (user && membership) {
        setMembers([{
          user_id: user.id,
          organization_id: activeOrg.id,
          role: membership.role as 'owner' | 'admin' | 'member',
          user: {
            email: user.primaryEmailAddress?.emailAddress || '',
            full_name: user.fullName || '',
            avatar_url: user.imageUrl
          }
        }]);
      }
    }
  }, [activeOrg, user, membership]);

  const switchOrganization = async (orgId: string) => {
    try {
      await setActive({ organization: orgId });
      const org = organizations.find(o => o.id === orgId);
      if (org) {
        setCurrentOrganization(org);
      }
    } catch (error) {
      // console.error('Error switching organization:', error);
      throw error;
    }
  };

  const createOrganization = async (name: string): Promise<Organization | null> => {
    // This is now handled by Clerk's CreateOrganization component
    // We keep this function for compatibility but it shouldn't be used
    return null;
  };

  const updateOrganization = async (name: string) => {
    if (!activeOrg) {
      throw new Error('No active organization');
    }

    try {
      await activeOrg.update({ name });
      
      // Update local state
      setCurrentOrganization(prev => prev ? { ...prev, name } : null);
      setOrganizations(prev => 
        prev.map(org => 
          org.id === activeOrg.id ? { ...org, name } : org
        )
      );

      // Sync with Supabase
      await supabase
        .from('organizations')
        .update({ name, updated_at: new Date().toISOString() })
        .eq('id', activeOrg.id);
    } catch (error) {
      // console.error('Error updating organization:', error);
      throw error;
    }
  };

  const inviteUser = async (email: string, role: 'admin' | 'member') => {
    if (!activeOrg) {
      throw new Error('No active organization');
    }

    try {
      await inviteMember({ emailAddress: email, role });
    } catch (error) {
      // console.error('Error inviting user:', error);
      throw error;
    }
  };

  const updateMemberRole = async (userId: string, role: 'admin' | 'member') => {
    // This would require using Clerk's API to update member roles
  };

  const removeMember = async (userId: string) => {
    // This would require using Clerk's API to remove members
  };

  return (
    <OrganizationContext.Provider
      value={{
        currentOrganization,
        organizations,
        members,
        loading,
        needsOnboarding,
        switchOrganization,
        createOrganization,
        updateOrganization,
        inviteUser,
        updateMemberRole,
        removeMember,
        refreshOrganizations
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
}

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};